// ブロック入札の CSV 出力 (入札一覧の「CSV ダウンロード」)。 spec `bidding/spot-bidding.md` §2 機能詳細 9、
// `conventions.md`「画面からダウンロードする CSV」。
//
// 列は CSV インポート (`bidding/spot-bid-block-csv-import.md` §1・§2) の取込と同じ列 (列名・順序・
// 値の表記) を先頭に並べ、 末尾にステータス列を 1 列足す。 末尾の列を削除すれば、 そのまま
// CSV インポートで再アップロードできる。 画面に出ている札は下書き・承認待ちを含めてすべて出す
// (確定していない札を落とさない) ので、 札の状態はステータス列で分かるようにする。
//
// 取込フォーマットの制約 (backend `block_bid_csv_parse.go` / `block_bid_csv.go`):
//   - 1 行ヘッダー: 0 行目 = カラム名 (完全一致)。 説明行は付けない (spec §1)
//   - 列は種別で変わる: 通常ブロック 56 列 / リンクブロック・ループ 58 列 (+ ペア番号, ブロック種別)
//   - 発注主体区分は行ごとの列 (AC-DSB-016)
//   - 売買方向は **「売買」列** (買 / 売) で表し、 コマ列は絶対値のみ (AC-DSB-005)
//   - LINK と LOOP は同一ファイルに混在できない (AC-DSB-012)
// このため種別ごとに別ファイルとして返す (= 札がある種別の分だけ)。
//
// 価格列・JEPX自由記述欄・排出係数紐付けは、 取り込むと同じ札に戻るように出す
// (spec 機能詳細 9):
//   - 価格: 手数料自動考慮が OFF なら入札価格、 ON なら backend が保存済みの入札価格から現在の手数料単価で
//     逆算した希望価格 ([[blockBidCsvPriceQueries]] で問い合わせる)
//   - 排出係数紐付けありの札: 排出係数紐付けに `1`、 JEPX自由記述欄に判別コメントの英字 1 文字
//     (判別コメントの後ろに書いた自由記述は出さない)
//   - 発注主体区分が分からない札 (eBalance 以外から取り込んだ札・CSV で指定できない区分) は空欄
import i18next from 'i18next'

import { KOMA_COUNT } from '~/constants/koma'
import { convertAreaLabel } from '~/models/area'
import {
  BID_CSV_STATUS_HEADER,
  BLOCK_BID_CSV_DATA_TYPE_NAME,
  BLOCK_BID_CSV_HEADER,
  BLOCK_CSV_TYPES,
  type BlockBidCsvKind,
  bidCsvLangOf,
  blockCsvLayoutOf,
} from '~/models/bidCsvHeader'
import { formatDateDisplay } from '~/models/dateTime'
import {
  type BidCsvPriceQuery,
  bidCsvEmissionCells,
  bidCsvIssuerTypeValue,
  bidCsvPriceCell,
  bidCsvSideValue,
} from '~/pages/SpotBidding/models/bidCsvCells'
import type { BidCsvExportInputs } from '~/pages/SpotBidding/models/bidCsvExportGate'
import { BID_STATUS_CATEGORY_LABEL_KEY } from '~/pages/SpotBidding/models/bidStatusCategory'
import type {
  BlockBidRow,
  BlockKind,
} from '~/pages/SpotBidding/models/blockBidRow'
import { deriveBlockBidStatusCategory } from '~/pages/SpotBidding/models/blockBidStatusCategory'
import { csvCell, neutralizeFormula } from '~/pages/SpotBidding/models/csvCell'

/** 出力する 1 ファイル。 */
export type BlockBidCsvFile = {
  filename: string
  csv: string
}

/**
 * 価格列の出所 (`price`) と自社の事業者 ID (`retailerId`: 排出係数紐付けの判別コメント) は
 * [[BidCsvExportInputs]]。 [[useBidCsvExportContext]] が出せると判定した時の値をそのまま展開して渡す。
 */
export type BuildBlockBidCsvFilesOptions = BidCsvExportInputs & {
  /** 受渡日 (= 画面で選択中の受渡日。 全行の受渡日列とファイル名に使う)。 */
  deliveryDate: Date
  /**
   * 入札締切 (受渡日前日 10:00 JST) を過ぎているか。 ステータス列の 11 区分導出
   * ([[deriveBlockBidStatusCategory]]、 AC-BSP-049) に、 画面 (BlockBidTable) と同じ値を渡す。
   * CSV の中で締切を判定し直すと、 締切をまたぐ間に画面と CSV の区分が食い違う。
   */
  isPastDeadline: boolean
}

/** 取込側の種別区分 (= 1 ファイル = 1 種別)。 */
const CSV_KIND_OF: Record<BlockKind, BlockBidCsvKind> = {
  STANDARD: 'standard',
  'LINK-P': 'link',
  'LINK-C': 'link',
  'LOOP-A': 'loop',
  'LOOP-B': 'loop',
}

/** `ブロック種別` 列の値 (backend `blockBidCsvLinkedTypes` / `blockBidCsvLoopTypes`)。 */
const BLOCK_TYPE_CELL: Record<BlockKind, string> = {
  STANDARD: '',
  'LINK-P': 'P',
  'LINK-C': 'C',
  'LOOP-A': 'A',
  'LOOP-B': 'B',
}

/**
 * leg 1 件をコマ 1〜48 のセル配列にする。
 *
 * 対象コマ (`slotCodes`) 以外は空セル (= 取込側で「対象外コマ」)。 コマ列は絶対値のみで
 * 符号を持たない (AC-DSB-005。 売買方向は `売買` 列が持つ)。 桁は取込側の制約に合わせて
 * 小数第 1 位まで。 コマ毎に量が違う札の数量を落とさない (issue #6633)。
 */
const komaCells = (row: BlockBidRow): string[] => {
  const volumeByKoma = new Map<number, number>()
  for (const [index, timeCode] of row.slotCodes.entries()) {
    volumeByKoma.set(timeCode, row.slotVolumes[index] ?? row.volumeMwh)
  }
  return Array.from({ length: KOMA_COUNT }, (_, i) => {
    const volume = volumeByKoma.get(i + 1)
    return volume === undefined ? '' : volume.toFixed(1)
  })
}

/** 価格列の逆算に問い合わせる leg (ブロック入札は成行が無いので全 leg)。 */
export const blockBidCsvPriceQueries = (
  rows: readonly BlockBidRow[],
): BidCsvPriceQuery[] =>
  rows.map((row) => ({
    direction: row.direction,
    orderPriceYenPerKwh: String(row.priceYenPerKwh),
  }))

/**
 * ブロック入札の leg 群を、 種別ごとの取込フォーマット CSV (+ 末尾のステータス列) に整形する。
 *
 * `rows` は画面の表示順で渡す (= LINK / LOOP は親 → 子の順。 取込側はペアを `ペア番号` で
 * 束ねるため行順自体には依存しないが、 人が読む時のために保つ)。 `ペア番号` は元 group ID
 * ではなくファイル内の連番にする (取込側の 20 文字上限に収め、 1 組ごとにファイル内で一意に
 * する。 取り込むと別 group として作り直されるため元 ID を持ち回る意味が無い)。
 *
 * 絞り込みで LOOP の片方の leg だけが渡された時 (A と B でエリアが違い、 エリアフィルタが
 * 片方だけを残す時) は、 渡された leg だけを出す。 その行を再アップロードすると、 ペアの
 * 行数の検証 (AC-DSB-009 / AC-DSB-010) で取込のエラーになる。
 *
 * 表示言語が英語のときは、 ヘッダー行を英語ヘッダー名、 区分値 (発注主体区分・エリア・
 * 売買・ステータス) を英語の値で出す (spec §2「英語表示対応」、 conventions.md「CSV の英語表示」)。
 * 取込は日本語・英語のどちらも受け付けるので、 どちらの言語で出しても取込にかけ直せる。
 * 受渡日・ペア番号・ブロック種別・数値は言語で変わらず、 自由記述の 2 列は訳さない。
 *
 * 札が無い種別のファイルは出さない (= 全種別が 0 件なら空配列)。
 */
export const buildBlockBidCsvFiles = (
  rows: readonly BlockBidRow[],
  options: BuildBlockBidCsvFilesOptions,
): BlockBidCsvFile[] => {
  const { deliveryDate, isPastDeadline, price, retailerId } = options
  // 取込は YYYY/MM/DD を保存時の形に揃えて受け付ける (spec §2 の受渡日)。
  const date = formatDateDisplay(deliveryDate)
  const fileDate = date.replaceAll('/', '')
  // エリア名を引く convertAreaLabel とステータス名を引く i18next が表示言語に従うので、
  // ヘッダー行と他の区分値も同じ言語で決める (1 つのファイルに日本語と英語を混ぜない)。
  // 言語を引数で受け取ると、 エリアとステータスだけが別の言語源になる。
  const lang = bidCsvLangOf(i18next.language.startsWith('en'))

  return BLOCK_CSV_TYPES.flatMap((kind) => {
    const target = rows.filter((row) => CSV_KIND_OF[row.kind] === kind)
    if (target.length === 0) return []

    const layout = blockCsvLayoutOf(kind)
    const isStandard = layout === 'standard'
    const header = [
      ...BLOCK_BID_CSV_HEADER[lang][layout],
      BID_CSV_STATUS_HEADER[lang],
    ]
    // ペア番号はファイル内の連番。 同一 group の 2 leg が同じ番号を持つ。
    const pairNoOf = new Map<string, number>()
    const body = target.map((row) => {
      if (!pairNoOf.has(row.groupId)) {
        pairNoOf.set(row.groupId, pairNoOf.size + 1)
      }
      // 受渡日 (+ ペア番号, ブロック種別) の後ろは通常ブロック・リンクブロック・ループで共通。
      // 価格・排出係数紐付け・JEPX自由記述欄は通常入札の CSV と同じ出し方 ([[bidCsvCells]])。
      // メモは数式インジェクション対策をかける (CWE-1236。 取込はセルの値を変換しない〈spec §2 の
      // 列 7・8〉ので、 先頭が = + - @ の値に足したアポストロフィは、 取り込み直すと値の先頭に残る)。
      const common = [
        bidCsvIssuerTypeValue(row.issuerType)?.[lang] ?? '',
        convertAreaLabel(row.areaCode),
        bidCsvSideValue(row.direction)[lang],
        bidCsvPriceCell(String(row.priceYenPerKwh), row.direction, price),
        ...bidCsvEmissionCells(row.memoJepx, retailerId),
        neutralizeFormula(row.memoFree ?? ''),
      ]
      const fixed = isStandard
        ? [date, ...common]
        : [
            date,
            String(pairNoOf.get(row.groupId)),
            BLOCK_TYPE_CELL[row.kind],
            ...common,
          ]
      // ステータス列は入札一覧のステータス列 (BlockBidTable のバッジ) と同じ区分名を、 同じ
      // i18n key から引く (AC-BSP-049。 表示言語が英語なら英語)。
      const status = i18next.t(
        BID_STATUS_CATEGORY_LABEL_KEY[
          deriveBlockBidStatusCategory(row, isPastDeadline)
        ],
      )
      return [...fixed, ...komaCells(row), status]
        .map((cell) => csvCell(cell))
        .join(',')
    })

    return [
      {
        filename: `${BLOCK_BID_CSV_DATA_TYPE_NAME[kind][lang]}_${fileDate}.csv`,
        csv: [header.join(','), ...body].join('\n'),
      },
    ]
  })
}
